/** Progressive enhancement: SSR/no-JS content stays visible. No scroll listener. */
const targets = [
  ".hero-copy", ".hero-visual", ".about-copy", ".about-image", ".sp-about-copy", ".sp-image",
  ".section-title", ".sp-center", ".service-grid>article", ".sp-services>article",
  ".gallery-grid>figure", ".gallery-grid>img", ".sp-gallery>figure", ".sp-gallery>img",
  ".testimonial-grid>blockquote", ".sp-testimonials blockquote", ".contact-copy",
  ".sp-contact>div:not(.location-map)", ".faq-items>.faq-item",
].join(",");

export function mountScrollReveal(root: HTMLElement): () => void {
  const win = root.ownerDocument.defaultView;
  if (!win || !win.IntersectionObserver || !win.matchMedia) return () => {};
  const reduced = win.matchMedia("(prefers-reduced-motion: reduce)");
  const nodes = new Set<HTMLElement>();
  const seen = new WeakSet<HTMLElement>();
  let observer: IntersectionObserver | undefined;
  const clear = () => {
    observer?.disconnect();
    nodes.forEach(node => {
      node.removeAttribute("data-scroll-reveal");
      node.removeAttribute("data-reveal-state");
      node.style.removeProperty("--reveal-delay");
    });
    nodes.clear();
  };
  const enter = (node: HTMLElement, immediate = false) => {
    seen.add(node);
    if (immediate) node.style.setProperty("--reveal-delay", "0ms");
    node.setAttribute("data-reveal-state", "entered");
    observer?.unobserve(node);
  };
  const setup = () => {
    clear();
    if (reduced.matches) return;
    try {
      observer = new win.IntersectionObserver(entries => {
        for (const entry of entries) if (entry.isIntersecting) enter(entry.target as HTMLElement);
      }, { threshold: 0, rootMargin: "0px 0px -24px 0px" });
      root.querySelectorAll<HTMLElement>(".designed-section").forEach(section => {
        const motion = ["subtle", "fade", "slide"].find(value => section.classList.contains(`section-motion-${value}`));
        if (!motion) return; // None/float and resolved local overrides are preserved.
        const matches = Array.from(section.querySelectorAll<HTMLElement>(targets));
        const selected = matches.filter(node => !matches.some(parent => parent !== node && parent.contains(node)));
        if (!selected.length && section.firstElementChild instanceof win.HTMLElement) selected.push(section.firstElementChild);
        selected.forEach((node, index) => {
          nodes.add(node);
          node.setAttribute("data-scroll-reveal", motion);
          // Leave above-fold and already-scrolled content visible, including LCP images.
          if (seen.has(node) || node.getBoundingClientRect().top < win.innerHeight - 24 || node.contains(root.ownerDocument.activeElement)) {
            enter(node, true);
          } else {
            node.style.setProperty("--reveal-delay", `${Math.min(index % 3 * 50, 100)}ms`);
            node.setAttribute("data-reveal-state", "pending");
            observer!.observe(node);
          }
        });
      });
    } catch {
      clear(); // Missing/broken browser enhancement must never hide a website.
    }
  };
  const focus = (event: FocusEvent) => {
    if (!(event.target instanceof win.HTMLElement)) return;
    const node = event.target.closest<HTMLElement>('[data-reveal-state="pending"]');
    if (node && nodes.has(node)) enter(node, true);
  };
  root.addEventListener("focusin", focus);
  reduced.addEventListener("change", setup);
  setup();
  return () => {
    root.removeEventListener("focusin", focus);
    reduced.removeEventListener("change", setup);
    clear();
  };
}
